import { useState, useMemo } from "react";
import { Funnel, MagnifyingGlass, CaretRight, CalendarBlank } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";

const norm = (v) => (v === null || v === undefined ? "" : String(v));
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export function parseDate(v) {
  const s = norm(v).trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return { y: +m[1], m: +m[2], d: +m[3] };
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})(?:\s|$)/);
  if (m) { const y = m[3].length === 2 ? 2000 + +m[3] : +m[3]; if (+m[2] >= 1 && +m[2] <= 12) return { y, m: +m[2], d: +m[1] }; }
  return null;
}

function buildTree(values) {
  const years = {}, other = [];
  values.forEach((v) => {
    const p = parseDate(v);
    if (!p) { other.push(v); return; }
    const Y = (years[p.y] ||= {});
    (Y[p.m] ||= []).push({ v, d: p.d });
  });
  return { years, other };
}

function TriBox({ vals, cur, setVals, testid }) {
  const n = vals.filter((v) => cur.has(v)).length;
  const state = n === 0 ? false : n === vals.length ? true : "indeterminate";
  return <Checkbox checked={state} data-testid={testid} onCheckedChange={() => setVals(vals, state !== true)} />;
}

function DateTree({ values, cur, setVals, colKey }) {
  const { years, other } = useMemo(() => buildTree(values), [values]);
  const [open, setOpen] = useState(() => new Set());
  const tog = (k) => setOpen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const yearKeys = Object.keys(years).sort((a, b) => b - a);
  return (
    <div className="space-y-0.5 pr-2" data-testid={`col-filter-datetree-${colKey}`}>
      {yearKeys.map((y) => {
        const months = years[y];
        const yVals = Object.values(months).flat().map((x) => x.v);
        return (
          <div key={y}>
            <div className="flex items-center gap-1.5 text-xs py-0.5">
              <button onClick={() => tog(y)} data-testid={`col-filter-year-toggle-${colKey}-${y}`}><CaretRight size={11} className={`transition-transform ${open.has(y) ? "rotate-90" : ""}`} /></button>
              <TriBox vals={yVals} cur={cur} setVals={setVals} testid={`col-filter-year-${colKey}-${y}`} />
              <span className="font-semibold">{y}</span><span className="text-muted-foreground">({yVals.length})</span>
            </div>
            {open.has(y) && Object.keys(months).sort((a, b) => a - b).map((mo) => {
              const mk = `${y}-${mo}`;
              const days = months[mo].sort((a, b) => a.d - b.d);
              return (
                <div key={mk} className="ml-4">
                  <div className="flex items-center gap-1.5 text-xs py-0.5">
                    <button onClick={() => tog(mk)} data-testid={`col-filter-month-toggle-${colKey}-${mk}`}><CaretRight size={11} className={`transition-transform ${open.has(mk) ? "rotate-90" : ""}`} /></button>
                    <TriBox vals={days.map((x) => x.v)} cur={cur} setVals={setVals} testid={`col-filter-month-${colKey}-${mk}`} />
                    <span>{MESI[mo - 1]}</span><span className="text-muted-foreground">({days.length})</span>
                  </div>
                  {open.has(mk) && days.map((x) => (
                    <label key={x.v} className="ml-8 flex items-center gap-2 text-xs py-0.5 cursor-pointer">
                      <Checkbox checked={cur.has(x.v)} onCheckedChange={() => setVals([x.v], !cur.has(x.v))} />
                      <span>{x.v}</span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}
      {other.length > 0 && (
        <div className="flex items-center gap-1.5 text-xs py-0.5 ml-4">
          <TriBox vals={other} cur={cur} setVals={setVals} testid={`col-filter-other-${colKey}`} />
          <span className="text-muted-foreground">(altri valori / vuoti) ({other.length})</span>
        </div>
      )}
    </div>
  );
}

export function ColumnFilter({ colKey, baseRows, allFilters, selected, onChange }) {
  const active = Array.isArray(selected) && selected.length > 0;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [checked, setChecked] = useState(null);

  const othersKey = JSON.stringify(
    Object.fromEntries(Object.entries(allFilters || {}).filter(([k, v]) => k !== colKey && Array.isArray(v) && v.length))
  );
  // Cascading: options reflect rows that pass the OTHER active column filters
  const optionRows = useMemo(() => {
    const others = Object.entries(allFilters || {}).filter(([k, v]) => k !== colKey && Array.isArray(v) && v.length);
    if (!others.length) return baseRows;
    return baseRows.filter((r) => others.every(([k, vals]) => vals.includes(norm(r[k]))));
  }, [baseRows, colKey, othersKey]);

  const values = useMemo(() => {
    const s = new Set();
    optionRows.forEach((r) => s.add(norm(r[colKey])));
    return [...s].sort((a, b) => a.localeCompare(b, "it", { numeric: true }));
  }, [optionRows, colKey]);

  const onOpen = (o) => {
    setOpen(o);
    if (o) { setQ(""); setChecked(new Set(active ? selected : values)); }
  };
  const cur = checked || new Set(active ? selected : values);
  const shown = values.filter((v) => !q || v.toLowerCase().includes(q.toLowerCase()));
  const toggle = (v) => setChecked(() => { const n = new Set(cur); if (n.has(v)) n.delete(v); else n.add(v); return n; });
  const setVals = (vals, on) => setChecked(() => { const n = new Set(cur); vals.forEach((v) => (on ? n.add(v) : n.delete(v))); return n; });
  const isDate = useMemo(() => {
    const ne = values.filter((v) => v !== "");
    return ne.length > 0 && ne.filter((v) => parseDate(v)).length / ne.length >= 0.7;
  }, [values]);
  const [dateView, setDateView] = useState(true);
  const useTree = isDate && dateView && !q;
  const apply = () => {
    const list = [...cur].filter((v) => values.includes(v));
    onChange(list.length === values.length ? [] : list);
    setOpen(false);
  };
  const reset = () => { onChange([]); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`col-filter-${colKey}`} onClick={(e) => e.stopPropagation()}
          className={`p-0.5 rounded transition-colors ${active ? "text-[hsl(var(--accent))]" : "text-muted-foreground/50 hover:text-foreground"}`}>
          <Funnel size={13} weight={active ? "fill" : "regular"} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="bg-popover border-border w-64 p-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1 mb-2 border-b border-border pb-2">
          <MagnifyingGlass size={13} className="text-muted-foreground" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid={`col-filter-search-${colKey}`}
            placeholder="Cerca valore..." className="flex-1 bg-transparent text-xs outline-none" />
        </div>
        <div className="flex gap-3 mb-2 text-[11px]">
          <button onClick={() => setChecked(new Set(values))} data-testid={`col-filter-all-${colKey}`} className="text-[hsl(var(--accent))]">Seleziona tutti</button>
          <button onClick={() => setChecked(new Set())} data-testid={`col-filter-none-${colKey}`} className="text-muted-foreground">Nessuno</button>
          {isDate && (
            <button onClick={() => setDateView((d) => !d)} data-testid={`col-filter-dateview-${colKey}`} className="ml-auto flex items-center gap-1 text-[hsl(var(--accent))]">
              <CalendarBlank size={12} /> {dateView ? "Elenco" : "Anno/Mese"}
            </button>
          )}
        </div>
        <ScrollArea className="h-44">
          {useTree ? <DateTree values={values} cur={cur} setVals={setVals} colKey={colKey} /> : (
          <div className="space-y-1 pr-2">
            {shown.map((v) => (
              <label key={v} className="flex items-center gap-2 text-xs py-0.5 cursor-pointer" data-testid={`col-filter-opt-${colKey}-${v || "empty"}`}>
                <Checkbox checked={cur.has(v)} onCheckedChange={() => toggle(v)} />
                <span className="truncate">{v === "" ? "(vuoto)" : v}</span>
              </label>
            ))}
            {shown.length === 0 && <div className="text-xs text-muted-foreground py-2">Nessun valore</div>}
          </div>
          )}
        </ScrollArea>
        <div className="flex justify-between gap-2 mt-2 border-t border-border pt-2">
          <Button size="sm" variant="ghost" onClick={reset} data-testid={`col-filter-reset-${colKey}`} className="text-xs h-7">Azzera</Button>
          <Button size="sm" onClick={apply} data-testid={`col-filter-apply-${colKey}`} className="text-xs h-7">Applica</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
